/**
 * Hariom ERP - Top Header Bar
 * Contains hamburger menu (mobile), page title, search, and notification bell
 */
import React, { useState, useMemo } from 'react'
import { Menu, Bell, Search, ChevronDown } from 'lucide-react'
import { useAuthStore } from '../../store/authStore'
import { useForecastStore } from '../../store/forecastStore'
import { useLocation, useNavigate } from 'react-router-dom'
import clsx from 'clsx'

// Map URL paths to human-readable page titles
const PAGE_TITLES: Record<string, string> = {
  '/dashboard':  'Dashboard',
  '/forecast':   'Demand Forecasting',
  '/purchase':   'Purchase',
  '/inventory':  'Inventory Management',
  '/production': 'Production Management',
  '/sales':      'Sales & CRM',
  '/quality':    'Quality Control',
  '/finance':    'Finance',
  '/hr':         'Human Resources',
  '/reports':    'Reports & Analytics',
  '/settings':   'Settings',
}

interface HeaderProps {
  onMenuToggle: () => void;  // Opens mobile sidebar
}

const Header: React.FC<HeaderProps> = ({ onMenuToggle }) => {
  const { user } = useAuthStore()
  const { contractors } = useForecastStore()
  const location = useLocation()
  const navigate = useNavigate()
  const [notifOpen, setNotifOpen] = useState(false)

  // ===== GLOBAL CONTRACTOR SEARCH =====
  const [query, setQuery] = useState('')
  const results = query.trim()
    ? contractors
        .filter(c => c.name.toLowerCase().includes(query.trim().toLowerCase()))
        .slice(0, 7)
    : []

  // Determine current page title from route
  const currentPath = '/' + location.pathname.split('/')[1]
  const pageTitle = PAGE_TITLES[currentPath] || 'ERP'

  // Live notifications derived from real demand-forecast data. Each one links
  // to the page it concerns, so clicking it takes the employee straight there.
  const notifications = useMemo(() => {
    const list: { id: string; type: string; title: string; message: string; time: string; link: string }[] = []
    contractors
      .filter(c => c.status === 'need_to_talk')
      .slice(0, 5)
      .forEach((c, i) =>
        list.push({
          id: `nt-${i}`,
          type: 'warning',
          title: 'Follow-up needed',
          message: `${c.name}: ${Math.abs(c.totalDiff)}T gap vs commitment`,
          time: 'Demand Forecast',
          link: '/forecast',
        }))
    if (list.length === 0)
      list.push({ id: 'ok', type: 'success', title: 'All on track', message: 'No contractors need follow-up right now', time: 'now', link: '/forecast' })
    return list
  }, [contractors])
  const unreadCount = notifications.length

  return (
    <header className="h-16 bg-white border-b border-steel-200 flex items-center px-4 gap-4 sticky top-0 z-30 shadow-sm">
      {/* ===== Mobile hamburger menu button ===== */}
      <button
        onClick={onMenuToggle}
        className="lg:hidden p-2 rounded-lg text-steel-500 hover:bg-steel-100 transition-colors"
        aria-label="Open navigation menu"
      >
        <Menu size={20} />
      </button>

      {/* ===== Page Title ===== */}
      <div className="flex-1">
        <h1 className="text-lg font-semibold text-steel-800">{pageTitle}</h1>
        {/* Breadcrumb date info */}
        <p className="text-xs text-steel-400 hidden sm:block">
          {new Date().toLocaleDateString('en-IN', {
            weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'
          })}
        </p>
      </div>

      {/* ===== Global Search Bar (searches contractors live) ===== */}
      <div className="hidden md:block relative w-72">
        <div className="flex items-center gap-2 bg-steel-50 border border-steel-200 rounded-lg px-3 py-1.5">
          <Search size={15} className="text-steel-400" />
          <input
            type="text"
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Search contractors..."
            className="bg-transparent text-sm text-steel-700 placeholder:text-steel-400 outline-none w-full"
          />
          {query && (
            <button
              onClick={() => setQuery('')}
              className="text-steel-400 hover:text-steel-600 text-sm leading-none"
              aria-label="Clear search"
            >
              ×
            </button>
          )}
        </div>

        {/* Results dropdown */}
        {query.trim() && (
          <div className="absolute left-0 right-0 top-full mt-1 bg-white rounded-xl shadow-lg
                          border border-steel-200 z-50 overflow-hidden max-h-80 overflow-y-auto">
            {results.length === 0 ? (
              <p className="px-4 py-3 text-sm text-steel-400">
                No contractor found for “{query}”
              </p>
            ) : (
              results.map(c => {
                const gap = c.totalDiff
                return (
                  <button
                    key={c.id}
                    onClick={() => { setQuery(''); navigate('/forecast') }}
                    className="w-full text-left px-4 py-2.5 border-b border-steel-50 hover:bg-steel-50 transition-colors"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-sm font-medium text-steel-800 truncate">{c.name}</span>
                      <span className={clsx(
                        'text-xs px-2 py-0.5 rounded-full flex-shrink-0',
                        c.status === 'need_to_talk' ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'
                      )}>
                        {c.status === 'need_to_talk' ? 'Need to Talk' : 'Active'}
                      </span>
                    </div>
                    <div className="text-xs text-steel-500 mt-0.5">
                      Forecast {c.totalForecast}T · Actual {c.totalActual}T · Diff{' '}
                      <span className={gap < 0 ? 'text-red-600 font-semibold' : 'text-green-600 font-semibold'}>
                        {gap >= 0 ? '+' : ''}{gap}T
                      </span>
                    </div>
                  </button>
                )
              })
            )}
          </div>
        )}
      </div>

      {/* ===== Notification Bell ===== */}
      <div className="relative">
        <button
          onClick={() => setNotifOpen(!notifOpen)}
          className="relative p-2 rounded-lg text-steel-500 hover:bg-steel-100 transition-colors"
          aria-label="View notifications"
        >
          <Bell size={20} />
          {/* Unread badge */}
          {unreadCount > 0 && (
            <span className="absolute top-1 right-1 w-4 h-4 bg-primary-600 text-white text-xs
                             rounded-full flex items-center justify-center font-semibold">
              {unreadCount}
            </span>
          )}
        </button>

        {/* Notification Dropdown */}
        {notifOpen && (
          <div className="absolute right-0 top-full mt-1 w-80 bg-white rounded-xl shadow-lg
                          border border-steel-200 z-50 overflow-hidden animate-fade-in">
            <div className="px-4 py-3 border-b border-steel-100 flex items-center justify-between">
              <h3 className="font-semibold text-steel-800">Notifications</h3>
              <span className="text-xs text-steel-500">{unreadCount} new</span>
            </div>
            <div className="max-h-72 overflow-y-auto">
              {notifications.map((n) => (
                <div
                  key={n.id}
                  onClick={() => { navigate(n.link); setNotifOpen(false) }}
                  className="px-4 py-3 border-b border-steel-50 hover:bg-steel-50
                             cursor-pointer transition-colors"
                >
                  {/* Colored dot by notification type */}
                  <div className="flex items-start gap-3">
                    <div className={`mt-1 w-2 h-2 rounded-full flex-shrink-0 ${
                      n.type === 'warning' ? 'bg-yellow-500' :
                      n.type === 'success' ? 'bg-green-500' : 'bg-blue-500'
                    }`} />
                    <div>
                      <p className="text-sm font-medium text-steel-800">{n.title}</p>
                      <p className="text-xs text-steel-500 mt-0.5">{n.message}</p>
                      <p className="text-xs text-steel-400 mt-1">{n.time}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="px-4 py-2 text-center">
              <button
                onClick={() => { navigate('/dashboard'); setNotifOpen(false) }}
                className="text-xs text-primary-600 font-medium hover:underline"
              >
                View all notifications
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ===== User Avatar (header) ===== */}
      {user && (
        <div className="flex items-center gap-2 cursor-pointer">
          <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center">
            <span className="text-primary-700 font-semibold text-xs">
              {user.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
            </span>
          </div>
          <div className="hidden sm:block">
            <p className="text-xs font-semibold text-steel-800 leading-tight">{user.name}</p>
            <p className="text-xs text-steel-400 capitalize leading-tight">
              {user.role.replace(/_/g, ' ')}
            </p>
          </div>
          <ChevronDown size={14} className="text-steel-400 hidden sm:block" />
        </div>
      )}
    </header>
  )
}

export default Header
